import dayjs from "dayjs";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface DiagnosisFields {
	dx: string; // Primary diagnosis (required)
	site: string; // Site and laterality (required)
	grade: string; // Grade or stage
	onset: string; // Onset date
	infection: string; // Infection status
	comorb: string; // Comorbidities
}

export interface DiagnosisDraft {
	uid: string;
	f: DiagnosisFields;
	author: string;
	srcId?: string;
	srcWhen?: string;
	version: number;
}

export interface DiagnosisRecord {
	uid: string;
	f: DiagnosisFields;
	confirmed: boolean;
	version: number;
	author: string;
	by: string;
	at: string;
	srcId?: string;
	srcWhen?: string;
}

export interface DiagnosisHistory {
	f: DiagnosisFields;
	version: number;
	by: string;
	at: string;
}

export interface PlanItem {
	uid: string;
	action: string;
	freq: string;
	who: "Patient" | "Clinic" | "";
	review: string;
	trig: string[];
	pf: boolean;
	route: "msg" | "ai";
	srcId?: string;
	srcWhen?: string;
	confirmed: boolean;
	pushed: boolean;
	by?: string;
	at?: string;
}

export const PROTOCOL_TRIGGERS = [
	"Spreading redness",
	"Fever",
	"Malodour",
	"Increasing pain",
	"New numbness",
	"Wound getting larger",
];

function cap(s: string) {
	if (!s) return "";
	return s.charAt(0).toUpperCase() + s.slice(1);
}

export function extractDx(text: string): DiagnosisFields {
	const t = text.toLowerCase();
	const f: DiagnosisFields = {
		dx: "",
		site: "",
		grade: "",
		onset: "",
		infection: "",
		comorb: "",
	};

	const conds = [
		"diabetic foot ulcer",
		"venous leg ulcer",
		"pressure ulcer",
		"surgical site infection",
		"cellulitis",
		"abscess",
	];
	for (const cond of conds) {
		if (t.includes(cond)) {
			f.dx = cap(cond);
			break;
		}
	}
	if (!f.dx && text.trim()) {
		const firstPart = text.split(/[,.;]/)[0]?.trim();
		if (firstPart) f.dx = cap(firstPart);
	}

	const parts =
		"(plantar|sole|heel|shin|toe|toes|forefoot|foot|calf|leg|sacrum|ankle|knee|thigh)";
	const near = new RegExp(`\\b(left|right|bilateral)\\s+${parts}\\b`).exec(t);
	const side = /\b(left|right|bilateral)\b/.exec(t);
	const part = new RegExp(`\\b${parts}\\b`).exec(t);
	if (near) {
		f.site = `${cap(near[1])} ${near[2]}`;
	} else if (side && part) {
		f.site = `${cap(side[1])} ${part[1]}`;
	} else if (side) {
		f.site = cap(side[1]);
	} else if (part) {
		f.site = cap(part[1]);
	}

	const g =
		/(?:wagner\s*(?:grade\s*)?(\d))/i.exec(t) ||
		/(?:(?:grade|stage)\s*(\d))/i.exec(t);
	if (g) {
		f.grade = /wagner/i.test(g[0]) ? `Wagner ${g[1]}` : `Grade ${g[1]}`;
	}

	const d =
		/(\d{1,2}\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+20\d\d)/i.exec(
			t,
		);
	if (d) {
		f.onset = d[1].replace(/\b\w/g, (c) => c.toUpperCase());
	}

	if (/no systemic|no signs? of infection|not infected|afebrile/.test(t)) {
		f.infection = "No systemic signs";
	} else if (/infected|purulent|malodour|febrile|cellulitis/.test(t)) {
		f.infection = "Signs of infection";
	}

	const co: string[] = [];
	if (/hypertension|\bhtn\b/.test(t)) co.push("Hypertension");
	if (/type 2 diabetes|\bt2dm\b/.test(t)) co.push("Type 2 diabetes");
	f.comorb = co.join(", ");

	return f;
}

export function extractPlan(text: string): Omit<PlanItem, "uid"> {
	const t = text.toLowerCase();
	const action = text.split(/[.;]/)[0]?.trim() || text.trim();
	let freq = "";
	const fr =
		/\b(twice daily|once daily|daily|weekly|twice weekly|every \d+ days|as needed)\b/.exec(
			t,
		);
	if (fr) freq = cap(fr[1]);

	let who: "Patient" | "Clinic" | "" = "";
	if (/patient|at home|self/.test(t)) who = "Patient";
	else if (/clinic|nurse|ward|district/.test(t)) who = "Clinic";

	let review = "";
	const d =
		/(\d{1,2}\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+20\d\d)/i.exec(
			t,
		);
	if (d) {
		review = d[1].replace(/\b\w/g, (c) => c.toUpperCase());
	}

	return {
		action,
		freq,
		who,
		review,
		trig: [],
		pf: false,
		route: "msg",
		confirmed: false,
		pushed: false,
	};
}

interface CaseClinicalData {
	dxRecord: DiagnosisRecord | null;
	dxDrafts: DiagnosisDraft[];
	dxHistory: DiagnosisHistory[];
	planItems: PlanItem[];
}

interface CaseClinicalStoreState {
	cases: Record<string | number, CaseClinicalData>;
}

interface CaseClinicalStoreActions {
	getCaseData: (caseId: string | number) => CaseClinicalData;
	addDiagnosisDraft: (
		caseId: string | number,
		text: string,
		author: string,
		srcId?: string,
		srcWhen?: string,
	) => string;
	updateDiagnosisField: (
		caseId: string | number,
		cardId: string, // 'rec' or draft uid
		field: keyof DiagnosisFields,
		value: string,
		currentUserName: string,
	) => void;
	confirmDiagnosis: (
		caseId: string | number,
		draftUid: string,
		currentUserName: string,
	) => boolean;
	discardDiagnosisDraft: (caseId: string | number, draftUid: string) => void;
	addPlanDraft: (
		caseId: string | number,
		item: Omit<PlanItem, "uid"> & { uid?: string },
	) => string;
	updatePlanField: (
		caseId: string | number,
		uid: string,
		field: "action" | "freq" | "review" | "who",
		value: string,
	) => void;
	togglePlanTrigger: (
		caseId: string | number,
		uid: string,
		trigger: string,
	) => void;
	togglePlanPatientFacing: (caseId: string | number, uid: string) => void;
	confirmPlanItem: (
		caseId: string | number,
		uid: string,
		currentUserName: string,
	) => boolean;
	discardPlanItem: (caseId: string | number, uid: string) => void;
	pushPlanToPatient: (caseId: string | number, uid: string) => void;
}

type CaseClinicalStore = CaseClinicalStoreState & CaseClinicalStoreActions;

const emptyCaseData = (): CaseClinicalData => ({
	dxRecord: null,
	dxDrafts: [],
	dxHistory: [],
	planItems: [],
});

let uidSeq = 0;
function generateUid() {
	uidSeq++;
	return `cl_${Date.now()}_${uidSeq}`;
}

export const useCaseClinicalStore = create<CaseClinicalStore>()(
	persist(
		(set, get) => ({
			cases: {},

			getCaseData: (caseId) => {
				const state = get();
				return state.cases[caseId] || emptyCaseData();
			},

			addDiagnosisDraft: (caseId, text, author, srcId, srcWhen) => {
				const uid = generateUid();
				const extracted = extractDx(text);
				const now = dayjs().format("hh:mm A");
				const when = srcWhen || "today";

				set((state) => {
					const prev = state.cases[caseId] || emptyCaseData();
					const newDraft: DiagnosisDraft = {
						uid,
						f: extracted,
						author: author || `your message · ${now}`,
						srcId,
						srcWhen: when,
						version: (prev.dxRecord?.version ?? 0) + 1,
					};
					return {
						cases: {
							...state.cases,
							[caseId]: {
								...prev,
								dxDrafts: [newDraft, ...prev.dxDrafts],
							},
						},
					};
				});
				return uid;
			},

			updateDiagnosisField: (caseId, cardId, field, value, currentUserName) => {
				set((state) => {
					const prev = state.cases[caseId] || emptyCaseData();
					const now = dayjs().format("D MMM YYYY, h:mm A");

					if (cardId === "rec" && prev.dxRecord) {
						if (prev.dxRecord.f[field] === value) return state;
						// Save old version to history and bump version
						const historyEntry: DiagnosisHistory = {
							f: { ...prev.dxRecord.f },
							version: prev.dxRecord.version,
							by: prev.dxRecord.by,
							at: prev.dxRecord.at,
						};
						return {
							cases: {
								...state.cases,
								[caseId]: {
									...prev,
									dxHistory: [...prev.dxHistory, historyEntry],
									dxRecord: {
										...prev.dxRecord,
										f: {
											...prev.dxRecord.f,
											[field]: value,
										},
										version: prev.dxRecord.version + 1,
										by: currentUserName || "Clinician",
										at: now,
									},
								},
							},
						};
					}

					// Otherwise edit draft
					const updatedDrafts = prev.dxDrafts.map((d) => {
						if (d.uid === cardId) {
							return {
								...d,
								f: {
									...d.f,
									[field]: value,
								},
							};
						}
						return d;
					});

					return {
						cases: {
							...state.cases,
							[caseId]: {
								...prev,
								dxDrafts: updatedDrafts,
							},
						},
					};
				});
			},

			confirmDiagnosis: (caseId, draftUid, currentUserName) => {
				const state = get();
				const prev = state.cases[caseId] || emptyCaseData();
				const draft = prev.dxDrafts.find((d) => d.uid === draftUid);
				if (!draft) return false;
				if (!draft.f.dx || !draft.f.site) return false;

				const now = dayjs().format("D MMM YYYY, h:mm A");
				const newHistory = [...prev.dxHistory];
				let newVersion = 1;

				if (prev.dxRecord) {
					newHistory.push({
						f: { ...prev.dxRecord.f },
						version: prev.dxRecord.version,
						by: prev.dxRecord.by,
						at: prev.dxRecord.at,
					});
					newVersion = prev.dxRecord.version + 1;
				}

				const confirmedRecord: DiagnosisRecord = {
					uid: draft.uid,
					f: { ...draft.f },
					confirmed: true,
					version: newVersion,
					author: draft.author,
					by: currentUserName || "Clinician",
					at: now,
					srcId: draft.srcId,
					srcWhen: draft.srcWhen,
				};

				set({
					cases: {
						...state.cases,
						[caseId]: {
							...prev,
							dxRecord: confirmedRecord,
							dxHistory: newHistory,
							dxDrafts: prev.dxDrafts.filter((d) => d.uid !== draftUid),
						},
					},
				});
				return true;
			},

			discardDiagnosisDraft: (caseId, draftUid) => {
				set((state) => {
					const prev = state.cases[caseId] || emptyCaseData();
					return {
						cases: {
							...state.cases,
							[caseId]: {
								...prev,
								dxDrafts: prev.dxDrafts.filter((d) => d.uid !== draftUid),
							},
						},
					};
				});
			},

			addPlanDraft: (caseId, item) => {
				const uid = item.uid || generateUid();
				const planItem: PlanItem = {
					...item,
					uid,
					confirmed: false,
					pushed: false,
				};

				set((state) => {
					const prev = state.cases[caseId] || emptyCaseData();
					return {
						cases: {
							...state.cases,
							[caseId]: {
								...prev,
								planItems: [planItem, ...prev.planItems],
							},
						},
					};
				});
				return uid;
			},

			updatePlanField: (caseId, uid, field, value) => {
				set((state) => {
					const prev = state.cases[caseId] || emptyCaseData();
					const updated = prev.planItems.map((p) => {
						if (p.uid === uid) {
							return { ...p, [field]: value };
						}
						return p;
					});
					return {
						cases: {
							...state.cases,
							[caseId]: {
								...prev,
								planItems: updated,
							},
						},
					};
				});
			},

			togglePlanTrigger: (caseId, uid, trigger) => {
				set((state) => {
					const prev = state.cases[caseId] || emptyCaseData();
					const updated = prev.planItems.map((p) => {
						if (p.uid === uid) {
							const exists = p.trig.includes(trigger);
							const nextTrig = exists
								? p.trig.filter((t) => t !== trigger)
								: [...p.trig, trigger];
							return { ...p, trig: nextTrig };
						}
						return p;
					});
					return {
						cases: {
							...state.cases,
							[caseId]: {
								...prev,
								planItems: updated,
							},
						},
					};
				});
			},

			togglePlanPatientFacing: (caseId, uid) => {
				set((state) => {
					const prev = state.cases[caseId] || emptyCaseData();
					const updated = prev.planItems.map((p) => {
						if (p.uid === uid) {
							return { ...p, pf: !p.pf };
						}
						return p;
					});
					return {
						cases: {
							...state.cases,
							[caseId]: {
								...prev,
								planItems: updated,
							},
						},
					};
				});
			},

			confirmPlanItem: (caseId, uid, currentUserName) => {
				const state = get();
				const prev = state.cases[caseId] || emptyCaseData();
				const item = prev.planItems.find((p) => p.uid === uid);
				if (!item || !item.action.trim()) return false;

				const now = dayjs().format("D MMM YYYY, h:mm A");
				const updated = prev.planItems.map((p) => {
					if (p.uid === uid) {
						return {
							...p,
							confirmed: true,
							by: currentUserName || "Clinician",
							at: now,
						};
					}
					return p;
				});

				set({
					cases: {
						...state.cases,
						[caseId]: {
							...prev,
							planItems: updated,
						},
					},
				});
				return true;
			},

			discardPlanItem: (caseId, uid) => {
				set((state) => {
					const prev = state.cases[caseId] || emptyCaseData();
					return {
						cases: {
							...state.cases,
							[caseId]: {
								...prev,
								planItems: prev.planItems.filter((p) => p.uid !== uid),
							},
						},
					};
				});
			},

			pushPlanToPatient: (caseId, uid) => {
				set((state) => {
					const prev = state.cases[caseId] || emptyCaseData();
					const updated = prev.planItems.map((p) => {
						if (p.uid === uid) {
							return { ...p, pushed: true };
						}
						return p;
					});
					return {
						cases: {
							...state.cases,
							[caseId]: {
								...prev,
								planItems: updated,
							},
						},
					};
				});
			},
		}),
		{
			name: "case-clinical-store",
		},
	),
);
